import { Controller } from "@hotwired/stimulus";

// Anchored popover controller for the +Question type-picker.
//
// Toggles `hidden` on every element with data-popover-target="hideable"
// (typically the panel and a mobile backdrop). Closes on Escape and on a
// click outside the controller's root element.
//
// Connect to: data-controller="popover"
//
// Usage:
//   <div data-controller="popover" class="relative">
//     <button data-action="click->popover#toggle">Open</button>
//     <div data-popover-target="hideable" class="hidden md:hidden fixed inset-0 bg-black/40"
//          data-action="click->popover#close"></div>
//     <div data-popover-target="hideable" class="hidden absolute right-0 top-full"
//          data-action="turbo:submit-end->popover#close">…</div>
//   </div>
export default class extends Controller {
  static targets = ["hideable", "trigger"];

  declare readonly triggerTarget: HTMLButtonElement;
  declare readonly hasTriggerTarget: boolean;
  declare readonly hideableTargets: HTMLElement[];

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener("keydown", this.handleKeydown);
    document.addEventListener("click", this.handleOutsideClick);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("click", this.handleOutsideClick);
  }

  toggle(event: Event) {
    event.stopPropagation();
    this.isOpen ? this.close() : this.open();
  }

  open() {
    if (this.element.classList.contains("dropdown")) this.element.classList.add("dropdown-open");
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", "true");
    this.hideableTargets.forEach((el) => el.classList.remove("hidden"));
  }

  close() {
    this.element.classList.remove("dropdown-open");
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", "false");
    this.hideableTargets.forEach((el) => el.classList.add("hidden"));
  }

  focusLeft(event: FocusEvent) {
    if (event.relatedTarget && !this.element.contains(event.relatedTarget as Node)) this.close();
  }

  private get isOpen(): boolean {
    return this.hideableTargets.some((el) => !el.classList.contains("hidden"));
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key !== "Escape" || !this.isOpen) return;
    this.close();
    if (this.hasTriggerTarget) {
      event.preventDefault();
      event.stopPropagation();
      this.triggerTarget.focus({ preventScroll: true });
    }
  }

  private handleOutsideClick(event: MouseEvent) {
    if (!this.isOpen) return;
    if (this.element.contains(event.target as Node)) return;
    this.close();
  }
}
